'use client';

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../../../../../lib/utils';

export type SwitchDualValue = 'left' | 'right';

export interface SwitchDualProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'onChange'
> {
  /** 현재 선택된 옵션 */
  value?: SwitchDualValue;
  /** 값 변경 핸들러 */
  onChange?: (value: SwitchDualValue) => void;
  /** 왼쪽 아이콘 */
  leftIcon: ReactNode;
  /** 오른쪽 아이콘 */
  rightIcon: ReactNode;
  /** 비활성화 상태 */
  disabled?: boolean;
}

/**
 * SwitchDual - 두 가지 옵션 중 하나를 선택하는 스위치 컴포넌트
 *
 * 라이트/다크 모드 전환과 같은 이진 옵션 선택에 사용됩니다.
 * 기존 Switch(on/off 토글)와 달리 두 개의 아이콘 옵션 중 하나를 선택합니다.
 */
const SwitchDual = forwardRef<HTMLButtonElement, SwitchDualProps>(
  (
    {
      className,
      value = 'left',
      onChange,
      leftIcon,
      rightIcon,
      disabled = false,
      ...props
    },
    ref,
  ) => {
    const handleClick = () => {
      if (disabled) return;
      const newValue: SwitchDualValue = value === 'left' ? 'right' : 'left';
      onChange?.(newValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (disabled) return;
      if (e.key === 'ArrowLeft' && value !== 'left') {
        onChange?.('left');
      } else if (e.key === 'ArrowRight' && value !== 'right') {
        onChange?.('right');
      }
    };

    return (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={value === 'right'}
        disabled={disabled}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={cn(
          // 컨테이너: 높이 32px, 패딩 4px, gap 2px, 곡률 999px
          'inline-flex h-[32px] items-center gap-[2px] rounded-[999px] p-[4px]',
          // 배경색: sys/style/neutral/lightest (#f9fafc)
          'bg-sys-style-neutral-lightest',
          'transition-colors duration-150',
          // 호버/프레스 상태
          !disabled && [
            'hover:bg-sys-style-neutral-light',
            'active:bg-sys-style-neutral-default',
          ],
          // 비활성화 상태
          disabled && 'cursor-not-allowed',
          // 포커스 스타일
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sys-style-primary-default focus-visible:ring-offset-2',
          className,
        )}
        {...props}
      >
        {/* 왼쪽 아이콘 래퍼: 24x24px, 패딩 4px, 곡률 999px */}
        <span
          className={cn(
            'flex size-[24px] shrink-0 items-center justify-center rounded-[999px] p-[4px]',
            'transition-all duration-150',
            value === 'left'
              ? [
                  // 선택된 상태
                  disabled
                    ? 'bg-sys-style-neutral-deeper'
                    : [
                        'bg-sys-style-primary-default',
                        // 그림자: shadow/thick/sm (0px 0px 8px rgba(4,4,5,0.16))
                        'shadow-[0px_0px_8px_0px_rgba(4,4,5,0.16)]',
                      ],
                  // 아이콘 색상: white (선택), sys-style-neutral-default (disabled 선택)
                  disabled
                    ? '[&_svg]:text-sys-style-neutral-default [&_path]:fill-sys-style-neutral-default'
                    : '[&_svg]:text-white [&_path]:fill-white',
                ]
              : [
                  // 선택 안된 상태: 아이콘 색상 sys/style/neutral/deeper (#babecf)
                  disabled
                    ? '[&_svg]:text-sys-style-neutral-default [&_path]:fill-sys-style-neutral-default'
                    : '[&_svg]:text-sys-style-neutral-deeper [&_path]:fill-sys-style-neutral-deeper',
                ],
          )}
        >
          {/* 아이콘: 16x16px */}
          <span className="flex size-[16px] items-center justify-center [&_svg]:size-[16px]">
            {leftIcon}
          </span>
        </span>

        {/* 오른쪽 아이콘 래퍼: 24x24px, 패딩 4px, 곡률 999px */}
        <span
          className={cn(
            'flex size-[24px] shrink-0 items-center justify-center rounded-[999px] p-[4px]',
            'transition-all duration-150',
            value === 'right'
              ? [
                  // 선택된 상태
                  disabled
                    ? 'bg-sys-style-neutral-deeper'
                    : [
                        'bg-sys-style-primary-default',
                        // 그림자: shadow/thick/sm (0px 0px 8px rgba(4,4,5,0.16))
                        'shadow-[0px_0px_8px_0px_rgba(4,4,5,0.16)]',
                      ],
                  // 아이콘 색상: white (선택), sys-style-neutral-default (disabled 선택)
                  disabled
                    ? '[&_svg]:text-sys-style-neutral-default [&_path]:fill-sys-style-neutral-default'
                    : '[&_svg]:text-white [&_path]:fill-white',
                ]
              : [
                  // 선택 안된 상태: 아이콘 색상 sys/style/neutral/deeper (#babecf)
                  disabled
                    ? '[&_svg]:text-sys-style-neutral-default [&_path]:fill-sys-style-neutral-default'
                    : '[&_svg]:text-sys-style-neutral-deeper [&_path]:fill-sys-style-neutral-deeper',
                ],
          )}
        >
          {/* 아이콘: 16x16px */}
          <span className="flex size-[16px] items-center justify-center [&_svg]:size-[16px]">
            {rightIcon}
          </span>
        </span>
      </button>
    );
  },
);

SwitchDual.displayName = 'SwitchDual';

export default SwitchDual;
